import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions,
  Button, Divider, Chip, CircularProgress, Alert, Tooltip,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  Download as DownloadIcon,
  TableChart as ExcelIcon,
  TextSnippet as TxtIcon,
  Summarize as SummarizeIcon,
  MenuBook as LibroIcon,
  ShoppingCart as ComprasIcon,
  PointOfSale as VentasIcon,
  AccountBalance as CajaIcon,
  Inventory as InventariosIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API = '/api/ple';

const MESES = [
  { v: 1, l: 'Enero' }, { v: 2, l: 'Febrero' }, { v: 3, l: 'Marzo' },
  { v: 4, l: 'Abril' }, { v: 5, l: 'Mayo' }, { v: 6, l: 'Junio' },
  { v: 7, l: 'Julio' }, { v: 8, l: 'Agosto' }, { v: 9, l: 'Septiembre' },
  { v: 10, l: 'Octubre' }, { v: 11, l: 'Noviembre' }, { v: 12, l: 'Diciembre' },
];

const ANIOS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

const LIBROS_MENSUALES = [
  {
    key: 'diario',
    codigo: '5.1',
    titulo: 'Libro Diario',
    subtitulo: 'LE050100',
    descripcion: 'Registro cronológico de todos los asientos contables del período.',
    icon: <LibroIcon sx={{ fontSize: 32 }} />,
    color: '#1E4D8C',
    colorBg: '#EBF3FF',
  },
  {
    key: 'diarioplancontable',
    codigo: '5.3',
    titulo: 'Diario — Plan Contable Utilizado',
    subtitulo: 'LE050300',
    descripcion: 'Complementario del Libro Diario: detalle de las cuentas contables usadas en el período. Obligatorio en enero o la primera vez que se genera el libro.',
    icon: <LibroIcon sx={{ fontSize: 32 }} />,
    color: '#1E4D8C',
    colorBg: '#EBF3FF',
  },
  {
    key: 'mayor',
    codigo: '6.1',
    titulo: 'Libro Mayor',
    subtitulo: 'LE060100',
    descripcion: 'Saldos acumulados por cuenta contable con movimientos y saldos progresivos.',
    icon: <SummarizeIcon sx={{ fontSize: 32 }} />,
    color: '#1A6B3A',
    colorBg: '#E8F5E9',
  },
  {
    key: 'diariosimplificado',
    codigo: '5.2',
    titulo: 'Diario Simplificado',
    subtitulo: 'LE050200',
    descripcion: 'Un asiento por fila, neto repartido en Activos/Pasivos/Patrimonio/Gastos/Ingresos (sin detalle cuenta por cuenta).',
    icon: <LibroIcon sx={{ fontSize: 32 }} />,
    color: '#5E3A1A',
    colorBg: '#F3E9DE',
  },
  {
    key: 'diariosimplificadoplancontable',
    codigo: '5.4',
    titulo: 'Diario Simplificado — Plan Contable Utilizado',
    subtitulo: 'LE050400',
    descripcion: 'Complementario del Diario Simplificado: detalle de las cuentas contables usadas en el período. Obligatorio en enero o la primera vez que se genera el libro.',
    icon: <LibroIcon sx={{ fontSize: 32 }} />,
    color: '#5E3A1A',
    colorBg: '#F3E9DE',
  },
  {
    key: 'cajaybancos',
    codigo: '1.1',
    titulo: 'Libro Caja y Bancos',
    subtitulo: 'LE010100',
    descripcion: 'Movimientos de cuentas de efectivo y bancos (cuentas del grupo 10).',
    icon: <CajaIcon sx={{ fontSize: 32 }} />,
    color: '#7B3F00',
    colorBg: '#FFF3E0',
  },
  {
    key: 'compras',
    codigo: '8.1',
    titulo: 'Registro de Compras',
    subtitulo: 'LE080100',
    descripcion: 'Registro de facturas y comprobantes de compra del período.',
    icon: <ComprasIcon sx={{ fontSize: 32 }} />,
    color: '#4A1A8C',
    colorBg: '#F3E5FF',
  },
  {
    key: 'ventas',
    codigo: '14.1',
    titulo: 'Registro de Ventas',
    subtitulo: 'LE140100',
    descripcion: 'Registro de comprobantes de venta emitidos: facturas, boletas, NC y ND.',
    icon: <VentasIcon sx={{ fontSize: 32 }} />,
    color: '#8C2A00',
    colorBg: '#FFECE5',
  },
];

const LIBROS_ANUALES = [
  {
    key: 'balancecomprobacion',
    codigo: '3.17',
    titulo: 'Balance de Comprobación',
    subtitulo: 'LE031700',
    descripcion: 'Saldos iniciales, movimientos y saldos finales del año, por cuenta.',
    icon: <InventariosIcon sx={{ fontSize: 32 }} />,
    color: '#1A3A5E',
    colorBg: '#E8EFF8',
  },
  {
    key: 'situacionfinanciera',
    codigo: '3.1',
    titulo: 'Estado de Situación Financiera',
    subtitulo: 'LE030100',
    descripcion: 'Balance general agrupado por rubro (requiere clasificar cuentas en Parámetros > Rubros EEFF).',
    icon: <InventariosIcon sx={{ fontSize: 32 }} />,
    color: '#0D5C4A',
    colorBg: '#E3F6F0',
  },
  {
    key: 'estadoresultados',
    codigo: '3.20',
    titulo: 'Estado de Resultados',
    subtitulo: 'LE032000',
    descripcion: 'Ingresos y gastos del año agrupados por rubro.',
    icon: <InventariosIcon sx={{ fontSize: 32 }} />,
    color: '#6B2E8C',
    colorBg: '#F3E8FB',
  },
  {
    key: 'resultadosintegrales',
    codigo: '3.24',
    titulo: 'Estado de Resultados Integrales',
    subtitulo: 'LE032400',
    descripcion: 'Igual al Estado de Resultados, más Otros Resultados Integrales (0 si no hay partidas de revaluación).',
    icon: <InventariosIcon sx={{ fontSize: 32 }} />,
    color: '#8C2E5C',
    colorBg: '#FBE8F0',
  },
  {
    key: 'flujoefectivodirecto',
    codigo: '3.18',
    titulo: 'Flujo de Efectivo — Método Directo',
    subtitulo: 'LE031800',
    descripcion: 'Cobros y pagos del año por categoría URC (requiere clasificar líneas de caja en Estado de Flujos de Efectivo).',
    icon: <CajaIcon sx={{ fontSize: 32 }} />,
    color: '#A35A00',
    colorBg: '#FFF1DE',
  },
  {
    key: 'cambiospatrimonio',
    codigo: '3.19',
    titulo: 'Cambios en el Patrimonio Neto',
    subtitulo: 'LE031900',
    descripcion: 'Saldo inicial, movimientos y saldo final del año por componente de patrimonio.',
    icon: <InventariosIcon sx={{ fontSize: 32 }} />,
    color: '#2E4C8C',
    colorBg: '#E8EEFB',
  },
];

function fmtNum(v, dec = 2) {
  if (v === undefined || v === null) return '—';
  return Number(v).toLocaleString('es-PE', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function ResumenChip({ label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" color="text.secondary">{label}:</Typography>
      <Typography variant="caption" fontWeight={600}>S/ {fmtNum(value)}</Typography>
    </Box>
  );
}

function LibroCard({ libro, empresaId, anio, mes, resumen, loadingResumen }) {
  const [loadingTxt, setLoadingTxt] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);

  const descargar = async (formato) => {
    const setLoading = formato === 'txt' ? setLoadingTxt : setLoadingExcel;
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId, anio, mes });
      const url = `/api/ple/${formato}/${libro.key}?${params}`;
      const resp = await axios.get(url, { responseType: 'blob' });
      const disposition = resp.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fname = match ? match[1] : `PLE_${libro.key}_${anio}${String(mes).padStart(2,'0')}.${formato === 'txt' ? 'txt' : 'xlsx'}`;
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fname;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert(`Error al descargar ${formato.toUpperCase()}: ${e.response?.data?.error || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const info = resumen?.[libro.key];

  return (
    <Card elevation={2} sx={{
      border: `1px solid ${libro.color}22`,
      borderTop: `4px solid ${libro.color}`,
      height: '100%',
      display: 'flex', flexDirection: 'column',
    }}>
      <CardContent sx={{ flexGrow: 1, pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
          <Box sx={{
            color: libro.color, bgcolor: libro.colorBg,
            borderRadius: 2, p: 1, display: 'flex', flexShrink: 0,
          }}>
            {libro.icon}
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label={libro.codigo} size="small" sx={{
                bgcolor: libro.color, color: '#fff',
                fontWeight: 700, fontSize: '0.7rem', height: 20,
              }} />
              <Chip label={libro.subtitulo} size="small" variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20, color: libro.color, borderColor: `${libro.color}66` }} />
            </Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5, color: libro.color }}>
              {libro.titulo}
            </Typography>
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}>
          {libro.descripcion}
        </Typography>

        <Divider sx={{ my: 0.5 }} />

        {/* Resumen de registros */}
        {loadingResumen ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">Calculando...</Typography>
          </Box>
        ) : info ? (
          <Box sx={{ mt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Chip
                label={`${info.count} registro${info.count !== 1 ? 's' : ''}`}
                size="small"
                color={info.count > 0 ? 'success' : 'default'}
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            </Box>
            {info.totales && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                {info.totales.debe !== undefined && <ResumenChip label="Debe" value={info.totales.debe} />}
                {info.totales.haber !== undefined && <ResumenChip label="Haber" value={info.totales.haber} />}
                {info.totales.total !== undefined && <ResumenChip label="Total" value={info.totales.total} />}
                {info.totales.bi !== undefined && <ResumenChip label="Base Imp." value={info.totales.bi} />}
                {info.totales.igv !== undefined && <ResumenChip label="IGV" value={info.totales.igv} />}
                {info.totales.gravada !== undefined && <ResumenChip label="Gravadas" value={info.totales.gravada} />}
                {info.totales.ingresos !== undefined && <ResumenChip label="Ingresos" value={info.totales.ingresos} />}
                {info.totales.egresos !== undefined && <ResumenChip label="Egresos" value={info.totales.egresos} />}
              </Box>
            )}
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">Sin datos</Typography>
        )}
      </CardContent>

      <CardActions sx={{ px: 1.5, pb: 1.5, pt: 0.5, gap: 1 }}>
        <Tooltip title={`Descargar ${libro.subtitulo}.txt (formato SUNAT PLE)`}>
          <span style={{ flex: 1 }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={loadingTxt ? <CircularProgress size={14} /> : <TxtIcon />}
              disabled={loadingTxt || !empresaId}
              onClick={() => descargar('txt')}
              sx={{ borderColor: libro.color, color: libro.color,
                '&:hover': { bgcolor: `${libro.color}11`, borderColor: libro.color } }}
            >
              PLE .TXT
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Descargar reporte Excel profesional">
          <span style={{ flex: 1 }}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              startIcon={loadingExcel ? <CircularProgress size={14} /> : <ExcelIcon />}
              disabled={loadingExcel || !empresaId}
              onClick={() => descargar('excel')}
              sx={{ bgcolor: libro.color, '&:hover': { bgcolor: libro.color + 'dd' } }}
            >
              Excel
            </Button>
          </span>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

function LibroAnualCard({ libro, empresaId, anio, resumenAnual }) {
  const [loadingTxt, setLoadingTxt] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);

  const descargar = async (formato) => {
    const setLoading = formato === 'txt' ? setLoadingTxt : setLoadingExcel;
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId, anio });
      const url = `/api/ple/${formato}/${libro.key}?${params}`;
      const resp = await axios.get(url, { responseType: 'blob' });
      const disposition = resp.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fname = match ? match[1] : `PLE_balancecomprobacion_${anio}.${formato === 'txt' ? 'txt' : 'xlsx'}`;
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fname;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert(`Error al descargar ${formato.toUpperCase()}: ${e.response?.data?.error || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card elevation={2} sx={{
      border: `1px solid ${libro.color}22`,
      borderTop: `4px solid ${libro.color}`,
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
          <Box sx={{
            color: libro.color, bgcolor: libro.colorBg,
            borderRadius: 2, p: 1, display: 'flex', flexShrink: 0,
          }}>
            {libro.icon}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label={libro.codigo} size="small" sx={{
                bgcolor: libro.color, color: '#fff',
                fontWeight: 700, fontSize: '0.7rem', height: 20,
              }} />
              <Chip label={libro.subtitulo} size="small" variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20, color: libro.color, borderColor: `${libro.color}66` }} />
              <Chip label="Anual" size="small" variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20, color: '#888', borderColor: '#bbb' }} />
            </Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5, color: libro.color }}>
              {libro.titulo}
            </Typography>
            <Typography variant="caption" color="text.secondary">{libro.descripcion}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={`Descargar ${libro.subtitulo}.txt (formato SUNAT PLE)`}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={loadingTxt ? <CircularProgress size={14} /> : <TxtIcon />}
                  disabled={loadingTxt || !empresaId}
                  onClick={() => descargar('txt')}
                  sx={{ borderColor: libro.color, color: libro.color }}
                >
                  PLE .TXT
                </Button>
              </span>
            </Tooltip>
            {!libro.soloTxt && (
              <Tooltip title="Descargar reporte Excel profesional">
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={loadingExcel ? <CircularProgress size={14} /> : <ExcelIcon />}
                    disabled={loadingExcel || !empresaId}
                    onClick={() => descargar('excel')}
                    sx={{ bgcolor: libro.color }}
                  >
                    Excel
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Ple() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [resumen, setResumen] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [errorResumen, setErrorResumen] = useState('');

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => {
      setEmpresas(r.data || []);
      if (r.data && r.data.length === 1) setEmpresaId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const cargarResumen = useCallback(async () => {
    if (!empresaId) return;
    setLoadingResumen(true);
    setErrorResumen('');
    try {
      const r = await axios.get(`${API}/resumen`, {
        params: { empresa_id: empresaId, anio, mes },
      });
      setResumen(r.data);
    } catch (e) {
      setErrorResumen(e.response?.data?.error || e.message);
      setResumen(null);
    } finally {
      setLoadingResumen(false);
    }
  }, [empresaId, anio, mes]);

  useEffect(() => {
    if (empresaId) cargarResumen();
  }, [cargarResumen, empresaId]);

  const empresaSeleccionada = empresas.find(e => e.id === empresaId);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Encabezado */}
      <Paper elevation={0} sx={{
        p: 2, mb: 2,
        background: 'linear-gradient(135deg, #1A3A5E 0%, #2E6DA4 100%)',
        borderRadius: 2, color: '#fff',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DownloadIcon sx={{ fontSize: 36, opacity: 0.9 }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Exportación PLE — Libros Electrónicos SUNAT
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Genera archivos .TXT en formato PLE y reportes Excel profesionales para cada libro contable
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Selectores de período */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.secondary' }}>
          PARÁMETROS DE GENERACIÓN
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Empresa Contable</InputLabel>
              <Select
                value={empresaId}
                label="Empresa Contable"
                onChange={e => setEmpresaId(e.target.value)}
              >
                {empresas.map(e => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.razon_social} <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>({e.ruc})</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Año</InputLabel>
              <Select value={anio} label="Año" onChange={e => setAnio(e.target.value)}>
                {ANIOS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Mes</InputLabel>
              <Select value={mes} label="Mes" onChange={e => setMes(e.target.value)}>
                {MESES.map(m => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="outlined"
              startIcon={loadingResumen ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={cargarResumen}
              disabled={!empresaId || loadingResumen}
              fullWidth
            >
              Calcular registros
            </Button>
          </Grid>
        </Grid>

        {empresaSeleccionada && (
          <Box sx={{ mt: 1.5, p: 1, bgcolor: '#F5F9FF', borderRadius: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption"><b>Empresa:</b> {empresaSeleccionada.razon_social}</Typography>
            <Typography variant="caption"><b>RUC:</b> {empresaSeleccionada.ruc}</Typography>
            <Typography variant="caption"><b>Período:</b> {MESES.find(m => m.v === mes)?.l} {anio}</Typography>
          </Box>
        )}

        {errorResumen && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setErrorResumen('')}>
            {errorResumen}
          </Alert>
        )}
      </Paper>

      {/* Libros mensuales */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: '#1A3A5E' }}>
        Libros Mensuales
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {LIBROS_MENSUALES.map(libro => (
          <Grid item xs={12} sm={6} md={4} key={libro.key}>
            <LibroCard
              libro={libro}
              empresaId={empresaId}
              anio={anio}
              mes={mes}
              resumen={resumen}
              loadingResumen={loadingResumen}
            />
          </Grid>
        ))}
      </Grid>

      {/* Libro de Inventarios y Balances (anual) */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: '#1A3A5E' }}>
        Libro de Inventarios y Balances (anual)
      </Typography>
      <Grid container spacing={2}>
        {LIBROS_ANUALES.map(libro => (
          <Grid item xs={12} sm={6} md={4} key={libro.key}>
            <LibroAnualCard
              libro={libro}
              empresaId={empresaId}
              anio={anio}
              resumenAnual={null}
            />
          </Grid>
        ))}
      </Grid>

      {/* Nota informativa */}
      <Paper elevation={0} sx={{
        mt: 2, p: 2, bgcolor: '#FFFDE7',
        border: '1px solid #FDD835', borderRadius: 2,
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}>
          INFORMACIÓN SOBRE EL FORMATO PLE
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          • Los archivos <b>.TXT</b> siguen el formato oficial SUNAT (campos separados por "|") y pueden cargarse directamente al software PLE SUNAT.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          • Los archivos <b>.XLSX</b> son reportes gerenciales profesionales con filtros automáticos, totales y formato visual de tabla dinámica.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          • El <b>Registro de Compras (6.1)</b> toma datos del módulo de Compras del ERP. El <b>Registro de Ventas (8.1)</b> toma datos del módulo de Comprobantes Electrónicos.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          • Los libros <b>Diario, Mayor y Caja y Bancos</b> se generan desde los asientos contables del módulo de Contabilidad.
        </Typography>
      </Paper>
    </Box>
  );
}
